import {Client, Event, Order, Product, Slot} from '../Models';
import {CartDTO, CheckoutDataDTO, CustomerDataDTO, OrderDTO} from '../DTOs';
import {UriBuilder} from './UriBuilder';
import {BaseModelService} from './BaseModelService';
import {RegistrationRelatedData} from '../store/typings';

export class OrderService extends BaseModelService<Order> {
  constructor(apiUrl: string = '') {
    super(Order);
    this.apiBaseUrl = `${apiUrl}/v2/order`;
  }

  async checkQuantity(orderId: string | number, buyerEmail: string): Promise<any> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path('orders')
      .path(orderId)
      .path('check-quantity')
      .build();
    const result = await this.restClient.POST(url, {orderId, buyerEmail}, {'content-type': 'application/json'});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async updateCart(orderDTO: OrderDTO, lang: string): Promise<CartDTO> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('orders')
      .path('webshop')
      .build();
    const result = await this.restClient.POST(url, orderDTO, {'content-type': 'application/json'});
    if (result.response) {
      return new CartDTO(result.response);
    }
    return this.throwAPIError(result.error);
  }

  async updateCheckoutData(checkoutDataDTO: CheckoutDataDTO, lang: string): Promise<CartDTO> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('orders')
      .path('webshop')
      .build();
    const result = await this.restClient.POST(url, checkoutDataDTO, {'content-type': 'application/json'});
    if (result.response) {
      return new CartDTO(result.response);
    }
    return this.throwAPIError(result.error);
  }

  async finishOrder(
    data: {
      orderId: number | string,
      token: string | null,
      applePayToken?: ApplePayJS.ApplePayPaymentToken | null,
      paypalOrderId?: string | null,
    },
    lang: string,
  ): Promise<any> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('orders')
      .path('webshop')
      .path(`${data.orderId}`)
      .path('finish')
      .build();
    const result = await this.restClient.POST(url, data, {'content-type': 'application/json'});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async redeemTicketCode(
    personalizationDto: CustomerDataDTO,
    userCodes: Array<string>,
    slots: Array<{ productId: number, slotId: number | string | null }>,
    lang: string,
  ) {
    const data = {
      customer: personalizationDto,
      codes: userCodes,
      slots: slots,
    };
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('tickets')
      .path('webshop')
      .path('redeem')
      .build();
    const result = await this.restClient.POST(url, data, {'content-type': 'application/json'});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async redeemUnclassifiedCode(code: string): Promise<CodeResult> {
    const data = {
      code: code,
    };
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path('codes')
      .path('check')
      .build();

    const result = await this.restClient.POST(url, data, {'content-type': 'application/json'});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async getRegistrationRelatedData(codes: Array<string>): Promise<RegistrationRelatedData> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path('tickets')
      .path('registration-data')
      .getParams({'codes': JSON.stringify(codes)})
      .build();
    const result = await this.restClient.GET(url, {'content-type': 'application/json'});
    if (result.response) {
      const relatedData: RegistrationRelatedData = {
        products: [],
        clients: [],
        events: [],
        slots: [],
        exhibitorData: {names: [], advertising: {}},
      };
      relatedData.events = result.response.events.map(e => new Event(e));
      relatedData.products = result.response.products.map(p => new Product(p));
      relatedData.clients = result.response.clients.map(c => new Client(c));
      relatedData.slots = result.response.slots.map(q => new Slot(q));
      relatedData.exhibitorData = result.response.exhibitorData;
      return relatedData;
    }
    return this.throwAPIError(result.error);
  }

  async getDownloadLinksWebshop(orderId: number | string, lang: string, authJWT: string): Promise<any> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('orders')
      .path('webshop')
      .path(`${orderId}`)
      .path('get-download-links')
      .build();
    const result = await this.restClient.POST(url, {}, {'content-type': 'application/json', 'Authorization': `Bearer ${authJWT}`});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async getDownloadLinksProgressWebshop(orderId: number | string, lang: string, authJWT: string): Promise<any> {
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('orders')
      .path('webshop')
      .path(`${orderId}`)
      .path('get-download-links-progress')
      .build();
    const result = await this.restClient.POST(url, {}, {'content-type': 'application/json', 'Authorization': `Bearer ${authJWT}`});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async getDownloadLinksRegistration(ticketCode: string, lang: string, authJWT: string): Promise<any> {
    const data = {
      ticketCode: ticketCode,
    };
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('tickets')
      .path('webshop')
      .path('get-download-links')
      .build();
    const result = await this.restClient.POST(url, data, {'content-type': 'application/json', 'Authorization': `Bearer ${authJWT}`});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }

  async getDownloadLinksProgressRegistration(ticketCode: string, lang: string, authJWT: string): Promise<any> {
    const data = {
      ticketCode: ticketCode,
    };
    const url = new UriBuilder()
      .fromPath(this.apiBaseUrl)
      .path(lang)
      .path('tickets')
      .path('webshop')
      .path('get-download-links-progress')
      .build();
    const result = await this.restClient.POST(url, data, {'content-type': 'application/json', 'Authorization': `Bearer ${authJWT}`});
    if (result.response) {
      return result.response;
    }
    return this.throwAPIError(result.error);
  }
}
